import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from './api';

/**
 * Loading data, without a data-fetching library.
 *
 * The panel reads about a dozen endpoints and mutates half of them. That is not
 * enough surface to earn a cache with its own invalidation model — what it needs is
 * "fetch this, tell me when it changed, and let me ask again after a write", which
 * is these forty lines. If the customer web app grows a feed that has to stay warm
 * across navigation, that is the moment to reach for something bigger.
 */
export interface Resource<T> {
  data: T | undefined;
  error: ApiError | null;
  /** True only on the first load, so a refresh does not blank the screen. */
  loading: boolean;
  /** True while any load is in flight, including a reload. */
  busy: boolean;
  reload: () => void;
}

export function useResource<T>(
  load: () => Promise<T>,
  deps: readonly unknown[],
  options: { enabled?: boolean } = {},
): Resource<T> {
  const enabled = options.enabled ?? true;
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(enabled);
  const [nonce, setNonce] = useState(0);

  // The loader closes over whatever the caller's deps are; the effect below is what
  // decides when to call it, so the reference itself must not be a dependency.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!enabled) {
      setBusy(false);
      return;
    }
    let live = true;
    setBusy(true);
    loadRef
      .current()
      .then((result) => {
        if (!live) return;
        setData(result);
        setError(null);
      })
      .catch((problem: unknown) => {
        if (!live) return;
        setError(
          problem instanceof ApiError
            ? problem
            : new ApiError('unknown', 'Что-то пошло не так'),
        );
      })
      .finally(() => {
        if (live) setBusy(false);
      });
    // A response for a request nobody is waiting for any more must not land on the
    // screen: the town selector changes faster than the network answers.
    return () => {
      live = false;
    };
    // The caller's `deps` are spread in on purpose: this hook cannot know what the
    // loader closes over, so the caller declares it, exactly as `useEffect` would.
  }, [enabled, nonce, ...deps]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return useMemo(
    () => ({ data, error, loading: busy && data === undefined, busy, reload }),
    [data, error, busy, reload],
  );
}

/**
 * A write, with the two things every button that performs one needs: whether it is
 * in flight, and what the server said when it refused.
 */
export interface Action<Args extends unknown[]> {
  run: (...args: Args) => Promise<boolean>;
  pending: boolean;
  error: ApiError | null;
  clearError: () => void;
}

export function useAction<Args extends unknown[]>(
  perform: (...args: Args) => Promise<unknown>,
  onDone?: () => void,
): Action<Args> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const performRef = useRef(perform);
  performRef.current = perform;
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  const run = useCallback(async (...args: Args): Promise<boolean> => {
    setPending(true);
    setError(null);
    try {
      await performRef.current(...args);
      doneRef.current?.();
      return true;
    } catch (problem) {
      setError(
        problem instanceof ApiError ? problem : new ApiError('unknown', 'Что-то пошло не так'),
      );
      return false;
    } finally {
      setPending(false);
    }
  }, []);

  return { run, pending, error, clearError: useCallback(() => setError(null), []) };
}

/** Closes a dialog on Escape, which is the only way out some of them need. */
export function useEscape(active: boolean, close: () => void): void {
  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, close]);
}
